import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import { BiCheck } from 'react-icons/bi';
import { FaArrowRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useGetProfileQuery } from '../../../app/services/auth-rtk.service';
import { useCreateContactUsMutation, useUploadToCDNMutation } from '../../../app/services/chatbot-rtk.services';
import Utilities from '../../../utils';

interface IFormData {
  email: string;
  message: string;
  website: 'G2' | 'Trustpilot' | 'SaaSworthy';
  link: string;
  screenshot: FileList;
  feature?: 'notificationBadge' | 'notificationSound' | 'notificationMessage' | 'operatingHours' | 'openChatbotByDefault' | 'extraSessions';
}

const RewardsPage = () => {
  const { t } = useTranslation();

  const features = [
    { value: 'notificationBadge', label: t('RewardsPage/key19') },
    { value: 'notificationMessage', label: t('RewardsPage/key25') },
    { value: 'notificationSound', label: t('RewardsPage/key20') },
    { value: 'operatingHours', label: t('RewardsPage/key21') },
    { value: 'openChatbotByDefault', label: t('RewardsPage/key22') },
    { value: 'extraSessions', label: t('RewardsPage/key26') },
  ];

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [countReviews, setCountReviews] = useState(1);

  const profileRequest = useGetProfileQuery();
  const [ContactUsRequest, ContactUsRequestStatus] = useCreateContactUsMutation();

  const [uploadToCDN, uploadToCDNStatus] = useUploadToCDNMutation();

  const schema = Yup.object({
    website: Yup.string().required(t('RewardsPage/key17')),
    link: Yup.string().url(),
    screenshot: Yup.mixed().optional(),
    feature: Yup.string()
      .oneOf(
        ['notificationBadge', 'notificationMessage', 'notificationSound', 'operatingHours', 'openChatbotByDefault', 'extraSessions'],
        t('RewardsPage/key23')
      )
      .default('notificationBadge')
      .required(t('RewardsPage/key23')),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    setFocus,
    reset,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: IFormData) => {
    const fileElement: HTMLInputElement | null = document.getElementById('fileUpload') as HTMLInputElement;
    let link: string | null = '';
    const formData = new FormData();

    if (!data.screenshot[0]) {
      toast.error(t('KbAssistantUploadFilesComponent/text-01'));
      return;
    }

    if (!Utilities.checkImageMimeType(data.screenshot[0])) {
      toast.error(t('VideoMacBotResponseForm/text-03'));
      return;
    }

    if (!Utilities.checkFileSize(5, data.screenshot[0])) {
      toast.error(t('AlertsTypeConstant/ISSTKO008', { size: '5 ' }));
      return;
    }

    formData.append('files', data.screenshot[0], profileRequest.data?.user_identification + '_' + data.screenshot[0].name);

    uploadToCDN(formData)
      .unwrap()
      .then((res: any[]) => {
        if (res && res.length > 0 && res[0]?.cdnPath) {
          link = res[0]?.cdnPath;
          const selectedFeatureLabel = features.find(feature => feature.value === data.feature)?.label;

          ContactUsRequest({
            email: data.email,
            message: `New Rewards for from ${data.email} , reward plateform: ${data.website} and link: ${link}, Selected feature: ${selectedFeatureLabel}`,
          })
            .unwrap()
            .then(() => {
              setShowSuccessMessage(true);
              setCountReviews(countReviews + 1);
              reset({
                email: data.email,
                message: undefined,
                website: undefined,
                link: undefined,
                screenshot: undefined,
              });
              setCountReviews(countReviews + 1);

              if (fileElement) fileElement.value = '';
            })
            .catch(err => {
              toast.error(err.response.data || err.message);
            });
        }
      })
      .catch(err => {
        toast.error(err.response.data || err.message);
      });
  };

  useEffect(() => {
    if (profileRequest.isSuccess) setValue('email', profileRequest.data.user_email);
  }, [profileRequest.isSuccess]);

  useEffect(() => {
    const st = setTimeout(() => {
      setShowSuccessMessage(false);
    }, 5000);

    return () => {
      clearTimeout(st);
    };
  }, [showSuccessMessage]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="tw-w-full tw-flex-col tw-justify-start tw-items-end tw-gap-6 tw-inline-flex tw-p-6">
        <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
          <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('RewardsPage/key1')}</div>
          <div className="tw-text-gray-800 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">{ReactHtmlParser(t('RewardsPage/key2'))}</div>
        </div>
        <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex tw-w-full">
          <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('RewardsPage/key3')}</div>
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
              <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('RewardsPage/key5')}</div>
                <div className="tw-self-stretch tw-text-gray-800 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"></div>
              </div>

              <Form.Group className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-4 tw-w-full tw-p-6 tw-bg-white tw-rounded-lg tw-shadow">
                {features.map(feature => (
                  <Form.Check
                    key={feature.value}
                    {...register('feature')}
                    type="radio"
                    defaultChecked={feature.value == 'notificationBadge' ? true : false}
                    value={feature.value}
                    label={feature.label}
                    id={`feature-${feature.value}`}
                    name="feature"
                    className="tw-text-start"
                  />
                ))}
              </Form.Group>

              <Form.Control.Feedback type="invalid">{errors.feature?.message}</Form.Control.Feedback>
            </div>
          </div>
          <div className="tw-self-stretch tw-h-[0px] tw-border tw-border-gray-300"></div>

          <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('RewardsPage/key6')}</div>
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
              <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('RewardsPage/key4')}</div>
                <div className="tw-self-stretch tw-text-gray-800 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"></div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-w-full">
                <div className="md:tw-col-span-3 tw-col-span-12 tw-p-6 tw-bg-white tw-rounded-xl tw-shadow tw-flex-col tw-justify-center tw-items-center tw-gap-4 tw-flex ">
                  <div className="tw-w-[29.18px] tw-h-[30px] tw-relative">
                    <div className="tw-w-[29.18px] tw-h-[30px] tw-left-[-0px] tw-top-[-0px] tw-absolute">
                      <img src="https://cdn.1cdn.app/application/LOUISEBOT/24062006463766_G2.svg" alt="g2" className="tw-w-full tw-h-full" />
                    </div>
                  </div>
                  <PSButton
                    id="btn-reward-open"
                    type="button"
                    className="tw-gap-2"
                    variant="outline"
                    onClick={() => {
                      window.open(`https://www.g2.com/products/louisebot/take_survey`, '_blank');

                      setValue('website', 'G2');

                      setFocus('website');
                    }}
                  >
                    G2 Crowd
                    <FaArrowRight className="tw-text-gray-400" />
                  </PSButton>
                </div>
                <div className="md:tw-col-span-3 tw-col-span-12 tw-p-6 tw-bg-white tw-rounded-xl tw-shadow tw-flex-col tw-justify-center tw-items-center tw-gap-4 tw-flex ">
                  <div className="tw-w-full tw-h-[30px] tw-relative">
                    <img src="https://cdn.1cdn.app/application/LOUISEBOT/24062006463792_Trustpilot.svg" alt="Trustpilot" className="tw-w-full tw-h-full" />
                  </div>
                  <PSButton
                    id="btn-reward-trust"
                    type="button"
                    className="tw-gap-2"
                    variant="outline"
                    onClick={() => {
                      window.open(`https://www.trustpilot.com/evaluate/louisebot.com`, '_blank');

                      setValue('website', 'Trustpilot');

                      setFocus('website');
                    }}
                  >
                    Trustpilot
                    <FaArrowRight className="tw-text-gray-400" />
                  </PSButton>
                </div>
                <div className="md:tw-col-span-3 tw-col-span-12  tw-p-6 tw-bg-white tw-rounded-xl tw-shadow tw-flex-col tw-justify-center tw-items-center tw-gap-4 tw-flex">
                  <div className="tw-w-full tw-h-[30px] tw-relative">
                    <img
                      src="https://cdn.1cdn.app/application/LOUISEBOT/24073012123897_sw-logo.png"
                      alt="SaaSworthy"
                      className="tw-w-full tw-h-full tw-object-contain"
                    />
                  </div>
                  <PSButton
                    id="btn-reward-saas"
                    type="button"
                    className="tw-gap-2"
                    variant="outline"
                    onClick={() => {
                      window.open(`https://www.saasworthy.com/product/louisebot#reviews`, '_blank');

                      setValue('website', 'SaaSworthy');

                      setFocus('website');
                    }}
                  >
                    SaaSworthy
                    <FaArrowRight className="tw-text-gray-400" />
                  </PSButton>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-self-stretch tw-h-[0px] tw-border tw-border-gray-300"></div>
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
            <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('RewardsPage/key18')}</div>
            <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
              <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
                <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                  <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('RewardsPage/key7')}</div>
                  <div className="tw-self-stretch tw-text-gray-800 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                    {t('RewardsPage/key8')}
                  </div>
                </div>
                <div className="tw-self-stretch tw-p-6 tw-bg-white tw-rounded-xl tw-shadow tw-flex-col tw-justify-center tw-items-start tw-gap-4 tw-flex">
                  <div className="tw-self-stretch tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                    {t('RewardsPage/key9')} #{countReviews}
                  </div>
                  <div className="tw-self-stretch  tw-flex-col tw-justify-center tw-items-start tw-gap-6 tw-flex">
                    <div className="tw-self-stretch  tw-flex-col tw-justify-center tw-items-start tw-gap-4 tw-flex">
                      <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-w-full">
                        <div className="md:tw-col-span-6 tw-col-span-12">
                          <Form.Group>
                            <Form.Label className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-tw-items-start tw-gap-2 tw-inline-flex">
                              <div className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('RewardsPage/key10')}</div>
                            </Form.Label>
                            <Form.Select
                              translate="no"
                              isInvalid={!!errors.website}
                              className="tw-self-stretch tw-pl-[17px] tw-pr-[13px] tw-py-[9px] tw-bg-white tw-rounded-xl tw-shadow tw-border tw-border-gray-300 tw-justify-between tw-items-center tw-inline-flex tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                              {...register('website')}
                            >
                              <option value="">{t('RewardsPage/key11')}</option>
                              <option value="G2">G2</option>
                              <option value="Trustpilot">Trustpilot</option>
                              <option value="SaaSworthy">SaaSworthy</option>
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.website?.message}</Form.Control.Feedback>
                          </Form.Group>
                        </div>
                        <div className="md:tw-col-span-6 tw-col-span-12">
                          <Form.Group className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                            <Form.Label className="tw-text-zinc-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                              {t('RewardsPage/key13')}
                            </Form.Label>

                            <Form.Control
                              id="fileUpload"
                              accept="image/jpeg, image/png, image/gif, image/bmp, image/webp"
                              isInvalid={!!errors.screenshot}
                              type="file"
                              className="tw-self-stretch tw-pl-[9px] tw-pr-[13px] tw-py-[9px] tw-bg-white tw-rounded-xl tw-shadow tw-border tw-border-gray-300 tw-justify-between tw-items-center tw-inline-flex"
                              {...register('screenshot')}
                            />

                            <Form.Control.Feedback type="invalid">{errors.screenshot?.message}</Form.Control.Feedback>
                          </Form.Group>
                        </div>
                      </div>
                    </div>
                    <div className="tw-self-stretch md:tw-justify-start tw-gap-2 tw-justify-between tw-items-center tw-inline-flex md:tw-flex-row tw-flex-col">
                      <PSButton
                        id="btn-reward-submit"
                        variant="none"
                        className="tw-text-white hover:tw-text-white tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal tw-px-[21px] tw-py-[7px] border tw-border-blue-600 tw-bg-primary hover:tw-bg-primary tw-rounded-[21px] tw-shadow md:tw-w-auto tw-w-full"
                        type="submit"
                        disabled={ContactUsRequestStatus.isLoading || uploadToCDNStatus.isLoading}
                        title={ContactUsRequestStatus.isLoading || uploadToCDNStatus.isLoading ? t('RewardsPage/key14') : t('RewardsPage/key15')}
                      />
                      {showSuccessMessage && (
                        <div className="tw-pl-1.5 tw-pr-4 tw-py-1.5 tw-bg-gray-100 tw-rounded-xl tw-shadow tw-justify-center tw-items-center tw-gap-2 tw-flex">
                          <div className="tw-w-6 tw-h-6 tw-pl-[5.29px] tw-pr-[4.29px] tw-pt-[7.29px] tw-pb-[5.59px] tw-justify-center tw-items-center tw-flex">
                            <BiCheck size={22} className="tw-text-green-700" />
                          </div>
                          <div className="tw-text-green-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">{t('RewardsPage/key16')}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default WrappedErrorBoundary(RewardsPage);
